import * as React from "react"
import { Box, Button, Checkbox, Chip, FormControl, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, TextField, debounce } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { AXIOS_API_INSTANCE } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import SearchIcon from '@mui/icons-material/Search';
import dayjs from "dayjs"
import onFilterChange from "../../Utils/OnFilterChange"
import { useSnackbar } from 'notistack';
import { DataGrid, } from "@mui/x-data-grid"
import ServiceOrderWarrantyModal from "./Modals/ServiceOrderWarrantyModal";
import { SimpleCustomToolbar } from "../CustomToolbar"
import { useNavigate } from "react-router-dom";
import DocumentTitle from "../../Utils/DocumentTitle";
import { widthChangeEvent } from "../../Utils"

const columns = (navigate) => {
    return [
        {
            field: 'claim_number',
            headerName: 'Claim Number',
            flex: 1,
            sortable: true,
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            sortable: true,
        },
        {
            field: 'date_created',
            headerName: 'Created',
            type: 'date',
            flex: 1,
            sortable: true,
            filterable: true,
            valueGetter: (value, row) => dayjs.utc(row.date_created).toDate()
        },
        {
            field: 'service_order_id',
            headerName: 'Service Order',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (<Button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/ServiceOrders/${params.value}`) }} href={`/ServiceOrders/${params.value}`}>Service Order #{params.value}</Button>)
            }
        },
    ]
}

const statuses = {
    'Created': { 'color': 'primary' },
    'Filed': { 'color': 'warning' },
    'Fulfilled': { 'color': 'success' },
}

export default function ServiceOrderWarrantiesList() {
    DocumentTitle('Service Order Warranties')
    const { enqueueSnackbar } = useSnackbar();
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: parseInt(localStorage.getItem(`serviceorderwarranties:pageSize`) || 10),
        page: 0,
    });
    const [rowData, setRowData] = React.useState({ rows: [], total_count: 0 })
    const [sorting, setSorting] = React.useState({ field: 'date_created', sort: 'desc' })
    const [filtering, setFiltering] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(true)
    const [serviceOrderWarrantySearch, setServiceOrderWarrantySearch] = React.useState('')
    const [includeStatuses, setIncludeStatuses] = React.useState(localStorage.getItem(`serviceorderwarranties:status_filters`) ? localStorage.getItem(`serviceorderwarranties:status_filters`).split(',') : ['Created', 'Filed'])
    const [newIncludeStatuses, setNewIncludeStatuses] = React.useState(localStorage.getItem(`serviceorderwarranties:status_filters`) ? localStorage.getItem(`serviceorderwarranties:status_filters`).split(',') : ['Created', 'Filed'])
    const [selectedWarranty, setSelectedWarranty] = React.useState(undefined)
    const [windowInnerWidth, setWindowInnerWidth] = React.useState(window.innerWidth)
    const navigate = useNavigate();

    const closeModal = (results) => {
        if (results) {
            getServiceOrderWarranties(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderWarrantySearch)
        }
        setSelectedWarranty(undefined)
    }

    const setMyPaginationModel = (model) => {
        localStorage.setItem(`serviceorderwarranties:pageSize`, model.pageSize)
        setPaginationModel(model)
    }

    const updateServiceOrderWarrantySearch = (event) => {
        setServiceOrderWarrantySearch(event.target.value.trim())
        if (event.target.value.trim() !== serviceOrderWarrantySearch) {
            debouncedGetServiceOrderWarranties(getServiceOrderWarranties, paginationModel, sorting.field, sorting.sort, filtering, event.target.value.trim())
        }
    }

    const getServiceOrderWarranties = (paginationModel, sortField, sortDirection, filterVar, serviceOrderWarrantySearch) => {
        setIsLoading(true)
        let my_filters = { "and": [] }
        if (Object.keys(filterVar).length > 0) {
            my_filters["and"].push(filterVar)
        }
        if (serviceOrderWarrantySearch.length > 0) {
            my_filters['and'].push({ 'claim_number.ilike': serviceOrderWarrantySearch })
        }
        my_filters["and"].unshift({ 'or': [] })
        includeStatuses.forEach((status) => {
            my_filters["and"][0]['or'].push({ 'status.eq': status })
        })
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("serviceorderwarranties", {
                params: {
                    page_num: paginationModel.page,
                    page_size: paginationModel.pageSize,
                    order_by: sortField,
                    order_by_direction: sortDirection,
                    filters: JSON.stringify(my_filters),
                    check_deletable: true
                },
            })
                .then((serviceorderwarranties) => {
                    setRowData({
                        rows: serviceorderwarranties.data.data,
                        total_count: serviceorderwarranties.data.total_count,
                    })
                    setIsLoading(false)
                }
                ).catch((error) => {
                    enqueueSnackbar(`Failed to load service order warranty claims: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                    setIsLoading(false)
                })
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetServiceOrderWarranties = React.useMemo(() => debounce((getServiceOrderWarranties, paginationModel, sortField, sortDirection, filterVar, serviceOrderWarrantySearch) => getServiceOrderWarranties(paginationModel, sortField, sortDirection, filterVar, serviceOrderWarrantySearch), 400), []);

    React.useEffect(() => {
        getServiceOrderWarranties(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderWarrantySearch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, filtering, sorting, includeStatuses])

    React.useEffect(() => {
        return widthChangeEvent(setWindowInnerWidth)
    }, [])

    return (
        (<Box sx={{ flexGrow: 1, flexShrink: 0, flexBasis: 0, overflow: 'hidden' }}>
            <Grid container spacing={1} alignItems={'flex-start'}>
                <Grid container columnSpacing={1} sx={{ mt: 0.5, mb: 0.5 }} direction="row" justifyContent="space-between" alignItems="center" size={12}>
                    <Grid size="auto">
                        <TextField
                            autoFocus
                            margin="dense"
                            id="contractsearch"
                            label="Search claim number"
                            type="text"
                            variant="filled"
                            disabled={isLoading}
                            value={serviceOrderWarrantySearch}
                            onChange={updateServiceOrderWarrantySearch}
                            slotProps={{
                                input: {
                                    inputProps: { min: 1 },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {<SearchIcon />}
                                        </InputAdornment>
                                    ),
                                }
                            }}
                        />
                    </Grid>
                    <Grid size="auto">
                        <FormControl sx={{ m: 1, minWidth: 300 }}>
                            <InputLabel id="serviceorder-warranty-statuses-filter-checkbox-label">Status Filter</InputLabel>
                            <Select
                                labelId="serviceorder-warranty-statuses-filter-checkbox-label"
                                id="serviceorder-warranty-statuses-filter-checkbox"
                                multiple
                                value={Object.keys(statuses).filter((x) => newIncludeStatuses.includes(x))}
                                onChange={(e) => setNewIncludeStatuses(e.target.value)}
                                onClose={(e) => {
                                    if (JSON.stringify(newIncludeStatuses.sort()) !== JSON.stringify(includeStatuses.sort())) {
                                        setIncludeStatuses(newIncludeStatuses)
                                        localStorage.setItem(`serviceorderwarranties:status_filters`, newIncludeStatuses)
                                    }
                                }}
                                disabled={isLoading}
                                input={<OutlinedInput id="select-multiple-chip" label="Status Filter" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} color={statuses[value].color} />
                                        ))}
                                    </Box>
                                )}
                            >
                                {Object.keys(statuses).map((name) => (
                                    <MenuItem key={name} value={name}>
                                        <Checkbox checked={newIncludeStatuses.includes(name)} />
                                        <Chip label={name} color={statuses[name].color} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid size={12}>
                    <DataGrid
                        key={windowInnerWidth}
                        slots={{
                            toolbar: () => SimpleCustomToolbar(),
                        }}
                        rows={rowData.rows}
                        rowCount={rowData.total_count}
                        columns={columns(navigate)}
                        disableRowSelectionOnClick
                        experimentalFeatures={{ newEditingApi: true }}
                        autoHeight={true}
                        filterMode={"server"}
                        loading={isLoading}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setMyPaginationModel}
                        onRowClick={(params) => setSelectedWarranty(params.row)}
                        onSortModelChange={(params) => {
                            if (params[0] !== undefined) {
                                setSorting(params[0])
                            } else {
                                setSorting({ field: 'id', sort: 'asc' })
                            }
                        }}
                        onFilterModelChange={(params) => { setFiltering(onFilterChange(params)) }}
                    />
                </Grid>
            </Grid>
            <ServiceOrderWarrantyModal
                serviceOrderWarranty={selectedWarranty ?? {}}
                open={selectedWarranty !== undefined}
                closeModal={closeModal}
                creatingNew={false}
                serviceOrderId={selectedWarranty?.service_order_id}
            />
        </Box>)
    );
}